/* global bootstrap */

import React from "react";
import Header from "./Header";
import Footer from "./Footer";
import axios from "axios";
import Swal from "sweetalert2";
import config from "../../config";
import * as XLSX from "xlsx";

const Billing_cod_remittance = () => {
  // =========================================================
  // USER
  // =========================================================

  const userDetail = localStorage.getItem("ship_rocket_user");

  let userData = {};

  if (userDetail) {
    try {
      userData = JSON.parse(userDetail);
    } catch (e) {
      console.log("User detail parse error ===", e);
    }
  }

  // =========================================================
  // STATES
  // =========================================================

  const [loading, setLoading] = React.useState(true);

  const [transaction, setTransaction] = React.useState([]);

  const [codremitted, setCodremitted] = React.useState({
    last_cod_remitted: 0,
    next_cod_remitted: 0,
    remittance_due: 0,
    remittance_till: 0,
  });

  const [balancestate, setBalanceState] = React.useState({
    available_balance: 0,
    hold_balance: 0,
    cod_collection: 0,
    cod_collection_mode: 1,
  });

  // Search
  const [searchText, setSearchText] = React.useState("");

  // Modal
  const [showModal, setShowModal] = React.useState(false);
  const [selectedPayoutId, setSelectedPayoutId] = React.useState("");

  const [payoutDetail, setPayoutDetail] = React.useState([]);
  const [shipmentDetail, setShipmentDetail] = React.useState([]);

  const [modalLoading, setModalLoading] = React.useState(false);

  // =========================================================
  // INITIAL LOAD
  // =========================================================

  React.useEffect(() => {
    getCodWalletBalance();
    getCodWalletTransactions();
  }, []);

  // =========================================================
  // GET COD WALLET BALANCE
  // =========================================================

  const getCodWalletBalance = async () => {
    try {
      const dataToSend = {
        customer_id: userData.customer_id,
      };

      const url = config.apiUrl + "/user/get_cod_wallet_balance";

      const res = await axios.post(url, dataToSend, {
        headers: config.headers,
      });

      if (res.data.status === true) {
        setBalanceState({
          available_balance: Number(res.data.available_balance || 0),

          hold_balance: Number(res.data.hold_balance || 0),

          cod_collection: Number(res.data.cod_collection || 0),

          cod_collection_mode: res.data.cod_collection_mode,
        });
      }
    } catch (error) {
      console.log("getCodWalletBalance error ===", error);
    }
  };

  // =========================================================
  // GET COD WALLET TRANSACTIONS
  // =========================================================

  const getCodWalletTransactions = async () => {
    try {
      setLoading(true);

      const dataToSend = {
        customer_id: userData.customer_id,
      };

      const url = config.apiUrl + "/shipment/get_cod_wallet_transactions";

      const res = await axios.post(url, dataToSend, {
        headers: config.headers,
      });

      if (res.data.status === true) {
        setTransaction(Array.isArray(res.data.output) ? res.data.output : []);

        setCodremitted({
          last_cod_remitted: res.data.last_cod_remitted || 0,

          next_cod_remitted: res.data.next_cod_remitted || 0,

          remittance_due: Number(res.data.due_remittance || 0),

          remittance_till: Number(res.data.remittance_till || 0),
        });
      } else {
        setTransaction([]);
      }
    } catch (error) {
      console.log("getCodWalletTransactions error ===", error);

      setTransaction([]);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // FORMAT CURRENCY
  // =========================================================

  const formatCurrency = (value) => {
    return Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // =========================================================
  // FORMAT DATE
  // =========================================================

  const formatDate = (timestamp) => {
    if (!timestamp) {
      return "-";
    }

    const date = new Date(Number(timestamp) * 1000);

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) {
      return "";
    }

    const date = new Date(Number(timestamp) * 1000);

    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  // =========================================================
  // FLATTEN TRANSACTIONS
  // =========================================================

  const getTransactionRows = React.useMemo(() => {
    const rows = [];

    transaction.forEach((payout) => {
      if (
        Array.isArray(payout.transactions) &&
        payout.transactions.length > 0
      ) {
        payout.transactions.forEach((item) => {
          rows.push({
            ...item,
            payout_id: payout.payout_id,
            create_date: payout.create_date,
          });
        });
      }
    });

    return rows;
  }, [transaction]);

  // =========================================================
  // FILTER TRANSACTIONS
  // =========================================================

  const filteredTransactions = React.useMemo(() => {
    if (!searchText.trim()) {
      return getTransactionRows;
    }

    const search = searchText.trim().toLowerCase();

    return getTransactionRows.filter((item) => {
      return (
        String(item.payout_id || "")
          .toLowerCase()
          .includes(search) ||
        String(item.utr_id || "")
          .toLowerCase()
          .includes(search) ||
        String(item.transaction_id || "")
          .toLowerCase()
          .includes(search)
      );
    });
  }, [getTransactionRows, searchText]);

  // =========================================================
  // OPEN PAYOUT MODAL
  // =========================================================

  const openPayoutModal = async (payoutId) => {
    if (!payoutId) {
      return;
    }

    setSelectedPayoutId(payoutId);

    setShowModal(true);

    setPayoutDetail([]);
    setShipmentDetail([]);

    await getPayoutDetail(payoutId);
  };

  // =========================================================
  // GET PAYOUT DETAIL
  // Same API used by Transaction_detail.jsx
  // =========================================================

  const getPayoutDetail = async (payoutId) => {
    try {
      setModalLoading(true);

      const dataToSend = {
        payout_id: payoutId,
      };

      const url = config.apiUrl + "/user/payout_detail_api";

      const res = await axios.post(url, dataToSend, {
        headers: config.headers,
      });

      setPayoutDetail(
        Array.isArray(res.data.payout_detail) ? res.data.payout_detail : []
      );

      setShipmentDetail(
        Array.isArray(res.data.shipments) ? res.data.shipments : []
      );
    } catch (error) {
      console.log("getPayoutDetail error ===", error);

      Swal.fire({
        icon: "error",
        title: "Something Went Wrong",
        text: "Unable to load payout details.",
      });

      setPayoutDetail([]);
      setShipmentDetail([]);
    } finally {
      setModalLoading(false);
    }
  };

  // =========================================================
  // CLOSE MODAL
  // =========================================================

  const closePayoutModal = () => {
    setShowModal(false);

    setSelectedPayoutId("");

    setPayoutDetail([]);
    setShipmentDetail([]);
  };

  // =========================================================
  // DOWNLOAD MAIN REPORT
  // =========================================================

  const handleDownload = () => {
    const excelData = [];

    transaction.forEach((sub) => {
      if (Array.isArray(sub.transactions) && sub.transactions.length > 0) {
        sub.transactions.forEach((item) => {
          excelData.push({
            Date: formatDate(sub.create_date),

            "Payout ID": sub.payout_id || "",

            "Total Amount": Number(item.total_cod_balance || 0).toFixed(2),

            "Wallet Adjusted": Number(item.wallet_adjusted_amount || 0).toFixed(
              2
            ),

            "Credit Wallet Adjusted": Number(
              item.credit_wallet_adjusted_amount || 0
            ).toFixed(2),

            "Early COD Commission": Number(
              item.early_cod_discount || 0
            ).toFixed(2),

            "Payout Amount": Number(item.payout_amount || 0).toFixed(2),

            "UTR No": item.utr_id || "",

            "Paid Date": item.paid_date || "",

            "Acc No": item.acc_no || "",

            "IFSC Code": item.ifsc_code || "",

            Status: item.status == 0 ? "Under Process" : "Completed",
          });
        });
      }
    });

    if (excelData.length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Data",
        text: "There is no payout data available to download.",
      });

      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Payout Report");

    XLSX.writeFile(workbook, "payout_report.xlsx");
  };

  // =========================================================
  // DOWNLOAD MODAL AWB REPORT
  // Same functionality as Transaction_detail.jsx
  // =========================================================

  const downloadPayoutAwbReport = () => {
    if (!Array.isArray(shipmentDetail) || shipmentDetail.length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Shipment Data",
        text: "There are no shipment details available for this payout.",
      });

      return;
    }

    const data = shipmentDetail.map((item) => ({
      "Shipment Delivered Date": item?.delivery_date || "",

      "Order Number": item?.order_number || "",

      "AWB Number": item?.carrier_tracking_number || "",

      "COD Amount": item?.cod_amount || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "COD AWB Report");

    XLSX.writeFile(workbook, "COD AWB Report.xlsx");
  };

  // =========================================================
  // LAST REMITTANCE AMOUNT
  // =========================================================

  const lastRemittanceAmount =
    codremitted.last_cod_remitted && codremitted.last_cod_remitted.payout_amount
      ? Number(codremitted.last_cod_remitted.payout_amount)
      : 0;

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div>
      <Header>
        <div className="content">
          {/* =================================================
              PAGE HEADER
          ================================================= */}

          <div className="ol-header">
            <div className="ol-hd">
              <button
                type="button"
                className="ol-back"
                title="Go back"
                onClick={() => window.history.back()}
              >
                <i className="bi bi-arrow-left"></i>
              </button>

              <div>
                <div className="ol-title">
                  COD Remittance{" "}
                  <small
                    style={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: "var(--t3)",
                    }}
                  >
                    (Future COD)
                  </small>
                </div>

                <div className="ol-sub">
                  Track your COD payouts and remittance history
                </div>
              </div>
            </div>
          </div>

          {/* =================================================
              KPI SUMMARY
          ================================================= */}

          <div className="ol-summary" id="codSummary">
            {/* COD TO BE REMITTED */}

            <div
              className="ol-metric"
              style={{
                "--m-bg": "rgba(14,164,114,.06)",
                "--m-br": "rgba(14,164,114,.18)",
              }}
            >
              <div className="m-label">
                <i className="bi bi-wallet2"></i> COD To Be Remitted
              </div>

              <div className="m-val">
                ₹{formatCurrency(balancestate.cod_collection)}
              </div>
            </div>

            {/* LAST REMITTED */}

            <div
              className="ol-metric"
              style={{
                "--m-bg": "rgba(37,99,235,.06)",
                "--m-br": "rgba(37,99,235,.18)",
              }}
            >
              <div className="m-label">
                <i className="bi bi-clock-history"></i> Last Remitted
              </div>

              <div className="m-val">
                ₹{formatCurrency(lastRemittanceAmount)}
              </div>
            </div>

            {/* TOTAL REMITTED */}

            <div
              className="ol-metric"
              style={{
                "--m-bg": "rgba(124,111,207,.06)",
                "--m-br": "rgba(124,111,207,.18)",
              }}
            >
              <div className="m-label">
                <i className="bi bi-check-circle"></i> Total Remitted
              </div>

              <div className="m-val">
                ₹{formatCurrency(codremitted.remittance_till)}
              </div>
            </div>

            {/* REMITTANCE DUE */}

            <div
              className="ol-metric"
              style={{
                "--m-bg": "rgba(232,64,64,.06)",
                "--m-br": "rgba(232,64,64,.18)",
              }}
            >
              <div className="m-label">
                <i className="bi bi-dash-circle"></i> Remittance Due
              </div>

              <div className="m-val">
                ₹{formatCurrency(codremitted.remittance_due)}
              </div>
            </div>
          </div>

          {/* =================================================
              SEARCH + TABLE
          ================================================= */}

          <div className="cod-remittance-wrap">
            <div className="cod-search-bar">
              <div className="cod-search">
                <i className="bi bi-search"></i>

                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search by Payout ID or UTR..."
                  autoComplete="off"
                />
              </div>

              <button
                type="button"
                className="cod-download-btn"
                title="Download report"
                onClick={handleDownload}
              >
                <i className="bi bi-download"></i>
              </button>
            </div>

            {/* =================================================
                TABLE
            ================================================= */}

            <div className="cod-table-wrap">
              <table className="cod-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Payout ID #</th>
                    <th>Total Amount</th>
                    <th>WALLET ADJ.</th>
                    <th>Credit WALLET ADJ.</th>
                    <th>Early COD Commission.</th>
                    <th>Payout</th>
                    <th>UTR #</th>
                    <th>Bank Details</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" className="text-center py-5">
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>

                        <div className="mt-2 text-muted">
                          Loading payouts...
                        </div>
                      </td>
                    </tr>
                  ) : filteredTransactions.length > 0 ? (
                    filteredTransactions.map((item, index) => (
                      <tr
                        key={
                          item.transaction_id || `${item.payout_id}-${index}`
                        }
                      >
                        {/* DATE */}

                        <td>
                          <div>{formatDate(item.create_date)}</div>

                          <small
                            style={{
                              color: "var(--t3)",
                            }}
                          >
                            {formatTime(item.create_date)}
                          </small>
                        </td>

                        {/* PAYOUT ID */}

                        <td>
                          <button
                            type="button"
                            className="cod-payout-link"
                            onClick={() => openPayoutModal(item.payout_id)}
                            style={{
                              border: "none",
                              background: "transparent",
                              padding: 0,
                              cursor: "pointer",
                            }}
                          >
                            <b>{item.payout_id}</b>
                          </button>
                        </td>

                        {/* TOTAL AMOUNT */}

                        <td>₹{formatCurrency(item.total_cod_balance)}</td>

                        {/* WALLET ADJUSTMENT */}

                        <td
                          className="fw-700"
                          style={{
                            color: "var(--danger, #e84040)",
                          }}
                        >
                          (-)₹
                          {formatCurrency(item.wallet_adjusted_amount)}
                        </td>

                        {/* CREDIT WALLET */}

                        <td
                          className="fw-700"
                          style={{
                            color: "var(--danger, #e84040)",
                          }}
                        >
                          ₹{formatCurrency(item.credit_wallet_adjusted_amount)}
                        </td>

                        {/* EARLY COD */}

                        <td
                          className="fw-700"
                          style={{
                            color: "var(--danger, #e84040)",
                          }}
                        >
                          ₹{formatCurrency(item.early_cod_discount)}
                        </td>

                        {/* PAYOUT */}

                        <td
                          className="fw-700"
                          style={{
                            color: "var(--success, #0ea472)",
                          }}
                        >
                          ₹{formatCurrency(item.payout_amount)}
                        </td>

                        {/* UTR */}

                        <td>
                          {item.utr_id ? (
                            <>
                              <span className="cod-utr">{item.utr_id}</span>

                              {item.paid_date && (
                                <small
                                  style={{
                                    display: "block",
                                    marginTop: "4px",
                                    color: "var(--t3)",
                                  }}
                                >
                                  {item.paid_date}
                                </small>
                              )}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>

                        {/* BANK */}

                        <td className="cod-bank">
                          <span>
                            {item.acc_no ? `Acc no: ${item.acc_no}` : "-"}
                          </span>

                          <span>
                            {item.ifsc_code ? `IFSC: ${item.ifsc_code}` : ""}
                          </span>
                        </td>

                        {/* STATUS */}

                        <td>
                          {item.status == 0 ? (
                            <span className="cod-badge">
                              <i className="bi bi-clock"></i> Under Process
                            </span>
                          ) : (
                            <span className="cod-badge cod-paid">
                              <i className="bi bi-check-circle"></i> Completed
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center py-5">
                        <div className="cod-empty">
                          <i className="bi bi-search"></i>

                          <p>
                            {searchText
                              ? "No payouts found matching your search."
                              : "No payout records found."}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* TABLE INFO */}

            <div className="cod-table-info">
              <span>
                Showing {filteredTransactions.length} payout
                {filteredTransactions.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>

          {/* =================================================
              PAYOUT DETAIL MODAL
          ================================================= */}

          {showModal && (
            <div
              className="cod-modal-overlay"
              id="codModalOverlay"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  closePayoutModal();
                }
              }}
              style={{
                display: "flex",
              }}
            >
              <div className="cod-modal">
                {/* ==========================================
                    MODAL HEADER
                ========================================== */}

                <div className="cod-modal-hd">
                  <div>
                    <div className="cod-modal-title">
                      Payout #{selectedPayoutId}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="cod-modal-close"
                    onClick={closePayoutModal}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>

                {/* ==========================================
                    MODAL BODY
                ========================================== */}

                <div className="cod-modal-body" id="codModalBody">
                  {modalLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>

                      <div className="mt-3 text-muted">
                        Loading payout details...
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* ====================================
                          TRANSACTION SUMMARY
                      ==================================== */}

                      <div className="cod-modal-txn">
                        <div className="table-responsive">
                          <table className="cod-modal-txn-table">
                            <thead>
                              <tr>
                                <th>Transaction #</th>

                                <th>Total COD</th>

                                <th>WALLET ADJ.</th>

                                <th>Credit WALLET ADJ.</th>

                                <th>Early COD Commission</th>

                                <th>Payout</th>

                                <th>Bank Details</th>

                                <th>UTR</th>

                                <th>Status</th>
                              </tr>
                            </thead>

                            <tbody>
                              {payoutDetail.length > 0 ? (
                                payoutDetail.map((item, index) => (
                                  <tr key={item.transaction_id || index}>
                                    <td>{item.transaction_id}</td>

                                    <td>
                                      ₹{formatCurrency(item.total_cod_balance)}
                                    </td>

                                    <td>
                                      (-)₹
                                      {formatCurrency(
                                        item.wallet_adjusted_amount
                                      )}
                                    </td>

                                    <td>
                                      ₹
                                      {formatCurrency(
                                        item.credit_wallet_adjusted_amount
                                      )}
                                    </td>

                                    <td>
                                      ₹{formatCurrency(item.early_cod_discount)}
                                    </td>

                                    <td>
                                      ₹{formatCurrency(item.payout_amount)}
                                    </td>

                                    <td>
                                      <span>Acc no: {item.acc_no}</span>

                                      <br />

                                      <span>IFSC Code: {item.ifsc_code}</span>
                                    </td>

                                    <td>{item.utr_id || "-"}</td>

                                    <td>
                                      {item.status == 0 ? (
                                        <span className="cod-badge">
                                          <i className="bi bi-clock"></i> Under
                                          Process
                                        </span>
                                      ) : (
                                        <span className="cod-badge cod-paid">
                                          <i className="bi bi-check-circle"></i>{" "}
                                          Completed
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="9" className="text-center py-4">
                                    No transaction details found.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* ====================================
                          TOTAL ORDERS + DOWNLOAD
                      ==================================== */}

                      <div className="cod-modal-order-count d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                          Total Orders <b>#{shipmentDetail.length}</b>
                        </div>

                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={downloadPayoutAwbReport}
                          disabled={shipmentDetail.length === 0}
                        >
                          <i className="bi bi-download me-1"></i>
                          Download Report
                        </button>
                      </div>

                      {/* ====================================
                          ORDER / SHIPMENT DETAILS
                      ==================================== */}

                      <div className="cod-modal-orders-wrap">
                        <div className="table-responsive">
                          <table className="cod-modal-orders-table">
                            <thead>
                              <tr>
                                <th>Shipment Delivered Date</th>

                                <th>AWB Number</th>

                                <th>Order Number</th>

                                <th>COD Amount</th>
                              </tr>
                            </thead>

                            <tbody>
                              {shipmentDetail.length > 0 ? (
                                shipmentDetail.map((item, index) => (
                                  <tr
                                    key={
                                      item.shipment_id ||
                                      item.carrier_tracking_number ||
                                      index
                                    }
                                  >
                                    <td>{item.delivery_date}</td>

                                    <td>
                                      <b>{item.carrier_tracking_number}</b>
                                    </td>

                                    <td>
                                      <b>{item.order_number}</b>
                                    </td>

                                    <td>₹{formatCurrency(item.cod_amount)}</td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="4" className="text-center py-4">
                                    No shipment details found.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Header>

      <Footer />
    </div>
  );
};

export default Billing_cod_remittance;
